import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { API_URL } from "../../config/apiConfig";

const FooterRoutes = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [formData, setFormData] = useState({
    category: "",
    order: 0,
  });
  const [routeFormData, setRouteFormData] = useState({
    from: "",
    to: "",
    label: "",
  });

  // Get auth token (support all user payload shapes)
  const getAuthToken = () => {
    return user?.token || user?.user?.token || user?.data?.token || null;
  };

  // Check if user is properly authenticated as admin
  const isAdminAuthenticated = () => {
    const role = user?.role || user?.user?.role || user?.data?.role;
    return Boolean(user && role === "admin");
  };

  // Check authentication on component mount
  useEffect(() => {
    if (!isAdminAuthenticated()) {
      toast.error("Please log in as admin to access this page");
      navigate("/admin/login");
      return;
    }
    fetchCategories();
  }, [navigate, user]);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();

      if (!token) {
        toast.error("Authentication required. Please log in again.");
        navigate("/admin/login");
        return;
      }

      const response = await axios.get(`${API_URL}/footer-routes/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data?.data || response.data;
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching footer routes:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired or access denied. Redirecting to login...");
        navigate("/admin/login");
      } else {
        toast.error(error.response?.data?.message || "Failed to load footer routes");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setFormData({
        category: category.category,
        order: category.order,
      });
      setCurrentCategory(category);
    } else {
      setFormData({
        category: "",
        order: 0,
      });
      setCurrentCategory(null);
    }
    setModalOpen(true);
  };

  const handleOpenRouteModal = (category) => {
    setCurrentCategory(category);
    setRouteFormData({
      from: "",
      to: "",
      label: "",
    });
    setRouteModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentCategory(null);
  };

  const handleCloseRouteModal = () => {
    setRouteModalOpen(false);
    setCurrentCategory(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "order" ? parseInt(value) || 0 : value,
    }));
  };

  const handleRouteChange = (e) => {
    const { name, value } = e.target;
    setRouteFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category.trim()) {
      toast.error("Category name is required!");
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        navigate("/admin/login");
        return;
      }

      const categoryData = {
        category: formData.category.trim(),
        order: formData.order,
      };

      if (currentCategory) {
        await axios.put(`${API_URL}/footer-routes/admin/${currentCategory._id}`, categoryData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Category updated successfully!");
      } else {
        await axios.post(`${API_URL}/footer-routes/admin`, categoryData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Category added successfully!");
      }

      handleCloseModal();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save category");
    }
  };

  const handleRouteSubmit = async (e) => {
    e.preventDefault();
    if (!routeFormData.from.trim() || !routeFormData.to.trim() || !routeFormData.label.trim()) {
      toast.error("All route fields are required!");
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        navigate("/admin/login");
        return;
      }

      await axios.post(
        `${API_URL}/footer-routes/admin/${currentCategory._id}/routes`,
        routeFormData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Route added successfully!");
      handleCloseRouteModal();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add route");
    }
  };

  const handleToggleStatus = async (categoryId, currentStatus) => {
    try {
      const token = getAuthToken();
      if (!token) {
        navigate("/admin/login");
        return;
      }

      await axios.put(
        `${API_URL}/footer-routes/admin/${categoryId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status updated successfully!");
      fetchCategories();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (window.confirm(`Are you sure you want to delete "${categoryName}"?`)) {
      try {
        const token = getAuthToken();
        if (!token) {
          navigate("/admin/login");
          return;
        }

        await axios.delete(`${API_URL}/footer-routes/admin/${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Deleted successfully!");
        fetchCategories();
      } catch (error) {
        toast.error("Failed to delete category");
      }
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const filteredCategories = categories.filter((category) =>
    category.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Premium Routes Management</h1>
        <p className="text-gray-600">Manage route categories and routes displayed in the footer section</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus /> Add Category
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading categories...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">No categories found</div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category._id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleCategoryExpansion(category._id)} className="text-gray-400 hover:text-gray-600">
                      {expandedCategories[category._id] ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{category.category}</h3>
                      <p className="text-sm text-gray-600">{category.routes.length} routes</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">Order: {category.order}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleStatus(category._id, category.isActive)}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${category.isActive ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-red-100 text-red-800 hover:bg-red-200"}`}
                    >
                      {category.isActive ? <FaToggleOn /> : <FaToggleOff />}
                      {category.isActive ? "Active" : "Inactive"}
                    </button>
                    <button onClick={() => handleOpenRouteModal(category)} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm hover:bg-green-200 flex items-center gap-1">
                      <FaPlus /> Add Route
                    </button>
                    <button onClick={() => handleOpenModal(category)} className="text-blue-600 hover:text-blue-800 p-1"><FaEdit /></button>
                    <button onClick={() => handleDelete(category._id, category.category)} className="text-red-600 hover:text-red-800 p-1"><FaTrash /></button>
                  </div>
                </div>
              </div>
              {expandedCategories[category._id] && (
                <div className="p-4 bg-gray-50">
                  {category.routes.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No routes added yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {category.routes.map((route, index) => (
                        <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                          <p className="font-medium text-gray-800">{route.label}</p>
                          <p className="text-sm text-gray-600">{route.from} → {route.to}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">{currentCategory ? "Edit Category" : "Add New Category"}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category Name *</label>
                  <input type="text" name="category" value={formData.category} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Order</label>
                  <input type="number" name="order" value={formData.order} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">{currentCategory ? "Update" : "Add"}</button>
                  <button type="button" onClick={handleCloseModal} className="flex-1 bg-gray-300 py-2 rounded-lg">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {routeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Add Route to "{currentCategory?.category}"</h2>
              <form onSubmit={handleRouteSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">From City *</label>
                  <input type="text" name="from" value={routeFormData.from} onChange={handleRouteChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">To City *</label>
                  <input type="text" name="to" value={routeFormData.to} onChange={handleRouteChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Label *</label>
                  <input type="text" name="label" value={routeFormData.label} onChange={handleRouteChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">Add Route</button>
                  <button type="button" onClick={handleCloseRouteModal} className="flex-1 bg-gray-300 py-2 rounded-lg">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterRoutes;
